import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, TableBody, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { capitalize, find, flatten, groupBy, intersection, isEmpty, map, sortBy, sum, sumBy, uniq } from 'lodash-es';
import React from 'react';
import NumberText, { formatNumber } from '~/components/NumberText';
import { formatUiDate } from '~/lib/date';
import { calculateDailyEnergy, calculateDailyPriceByFeePeriod } from '~/models/common';
import { calculateWeightedRatesByEnergyFromSegments } from '../weightedRateUtils';
import { CalculatedProjectOffer, ReportProjectMeter } from './reportTypes';
import {
  NoPageBreakBefore,
  NoPageBreakInside,
  PageBreakBefore,
  Section,
  SectionContainer,
  StyledTable,
  StyledTableCell,
  Title,
  TitleUnderline
} from './style';

type SegmentLike = {
  startDate?: string;
  endDate?: string;
  numOfDays?: number;
  peak?: number;
  offPeak?: number;
  shoulder?: number;
  lret?: number;
  sres?: number;
  ess?: number;
  veet?: number;
  aeeis?: number;
  pdrs?: number;
  reps?: number;
  peakConsumptionIncLoss?: number;
  offPeakConsumptionIncLoss?: number;
  shoulderConsumptionIncLoss?: number;
  totalConsumptionIncLoss?: number;
};

function renderSegmentsTooltipContent(segments: SegmentLike[], keyPrefix: string): React.ReactNode {
  if (!segments?.length) return null;
  return (
    <Box sx={{ p: 1, maxHeight: '400px', overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Segments Details ({segments.length} segments)
      </Typography>
      {segments.map((segment, idx) => (
        <Box
          key={`${keyPrefix}-${idx}`}
          sx={{
            mb: 1.5,
            pb: 1.5,
            borderBottom: idx < segments.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
            Segment {idx + 1}: {formatUiDate(segment.startDate)} - {formatUiDate(segment.endDate)} ({segment.numOfDays}{' '}
            days)
          </Typography>
          <Box sx={{ pl: 1, fontSize: '0.75rem' }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              <strong>Contract Rates (c/kWh):</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
              Peak: {formatNumber(segment.peak ?? 0, 4, '-')}, Off-Peak: {formatNumber(segment.offPeak ?? 0, 4, '-')},
              Shoulder: {formatNumber(segment.shoulder ?? 0, 4, '-')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              <strong>Environmental Rates (c/kWh):</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
              LRET: {formatNumber(segment.lret ?? 0, 4, '-')}, SRES: {formatNumber(segment.sres ?? 0, 4, '-')}, ESS:{' '}
              {formatNumber(segment.ess ?? 0, 4, '-')}, VEET: {formatNumber(segment.veet ?? 0, 4, '-')}, AEEIS:{' '}
              {formatNumber(segment.aeeis ?? 0, 4, '-')}, PDRS: {formatNumber(segment.pdrs ?? 0, 4, '-')}, REPS:{' '}
              {formatNumber(segment.reps ?? 0, 4, '-')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              <strong>Consumption kWh:</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
              Peak: {formatNumber((segment as any).peakConsumptionExcludeLoss ?? 0, 0, '-')}, Off-Peak:{' '}
              {formatNumber((segment as any).offPeakConsumptionExcludeLoss ?? 0, 0, '-')}, Shoulder:{' '}
              {formatNumber((segment as any).shoulderConsumptionExcludeLoss ?? 0, 0, '-')}, Total:{' '}
              {formatNumber((segment as any).totalConsumptionExcludeLoss ?? 0, 0, '-')}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const ContractTermComparisonSection: React.FC<{
  meters: ReportProjectMeter[];
  offers: CalculatedProjectOffer[];
  name: string;
  currentContestableCost: number;
  /** When true, show segment-detail help icon on table rows (preview only; hidden in print/share). */
  showSegmentHelp?: boolean;
}> = ({ meters, offers, name, currentContestableCost, showSegmentHelp = false }) => {
  // Table shows all retailers (no best-period filter). (retailer, term) visibility is enforced in aggregateForDisplay.
  const offersWithAllMeters = offers.filter((offer) => (offer.periods || []).length > 0);

  /** When period has per-meter rates and they differ, show one line per meter; otherwise single value. */
  const renderRateCell = (
    period: {
      meterPeakRates?: Record<string, number>;
      meterOffPeakRates?: Record<string, number>;
      meterShoulderRates?: Record<string, number>;
    },
    rateKey: 'peak' | 'offPeak' | 'shoulder',
    singleVal: number
  ) => {
    const meterRates =
      rateKey === 'peak'
        ? period.meterPeakRates
        : rateKey === 'offPeak'
        ? period.meterOffPeakRates
        : period.meterShoulderRates;
    const values = meterRates ? Object.values(meterRates) : [];
    const showMultiLine = meterRates && uniq(values).length > 1 && Object.keys(meterRates).length > 0;
    if (showMultiLine && meterRates) {
      return (
        <Box component="span" sx={{ display: 'block' }}>
          {Object.entries(meterRates).map(([meterId, val]) => {
            const label = meters.find((m) => m.id === meterId)?.meterNumber ?? meterId;
            return (
              <Typography key={meterId} component="span" variant="body2" display="block">
                <NumberText value={val} precision={4} na="-" /> ({label})
              </Typography>
            );
          })}
        </Box>
      );
    }
    return <NumberText value={singleVal} precision={4} na="-" />;
  };

  return (
    <>
      <Section id="contract-period-comparison" sx={{ ...PageBreakBefore, ...NoPageBreakInside }}>
        <SectionContainer sx={{ textAlign: 'center' }}>
          <Title variant="h4">CONTRACT TERM COMPARISON</Title>
          <TitleUnderline />
          <Typography variant="h5">{name}</Typography>
          <Typography variant="caption" gutterBottom component="span">
            <i>{map(meters, 'meterNumber').filter(Boolean).join(', ') || '-'}</i>
          </Typography>
        </SectionContainer>
      </Section>

      <Section sx={NoPageBreakBefore}>
        <SectionContainer>
          {Object.entries(groupBy(flatten(map(offersWithAllMeters, 'periods')), 'periodName')).map(
            ([periodName, periods], index) => {
              const sortedPeriods = sortBy(periods, 'annualTotalCosts');
              return (
                <Box key={`period-comparison-${index}`} sx={{ mb: 4, ...NoPageBreakInside }}>
                  <Typography variant="h6" gutterBottom>
                    {periodName} Months
                  </Typography>
                  <TableContainer>
                    <StyledTable>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell></StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                          <StyledTableCell>Rate Type</StyledTableCell>
                          <StyledTableCell colSpan={3}>kWh Commodity Unit Rate (weighted avg)</StyledTableCell>
                          <StyledTableCell>Contract Cost</StyledTableCell>
                          <StyledTableCell>Environmental Costs</StyledTableCell>
                          <StyledTableCell>Retailer & Metering Costs</StyledTableCell>
                          <StyledTableCell>Contestable Total Spend</StyledTableCell>
                          <StyledTableCell>Differential</StyledTableCell>
                          <StyledTableCell>Cost</StyledTableCell>
                          {currentContestableCost > 0 && (
                            <>
                              <StyledTableCell>$ Change</StyledTableCell>
                              <StyledTableCell>% Change</StyledTableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{ bgcolor: 'brand.main' }}>
                          <StyledTableCell sx={{ color: 'common.white' }}>Offer Rank</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Retailer</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Offered</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Peak</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Off Peak</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Shoulder</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                          <StyledTableCell sx={{ color: 'common.white' }}>c/kWh</StyledTableCell>
                          {currentContestableCost > 0 && (
                            <>
                              <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                              <StyledTableCell sx={{ color: 'common.white' }}>%</StyledTableCell>
                            </>
                          )}
                        </TableRow>
                        {map(sortedPeriods, (period, periodIndex) => {
                          const segments = (period.environmentalRatePeriods ?? []) as SegmentLike[];
                          const dailyPeak = sumBy(meters, (m) => calculateDailyEnergy(m.annualData?.peakEnergy ?? 0));
                          const dailyOffPeak = sumBy(meters, (m) =>
                            calculateDailyEnergy(m.annualData?.offPeakEnergy ?? 0)
                          );
                          const dailyShoulder = sumBy(meters, (m) =>
                            calculateDailyEnergy(m.annualData?.shoulderEnergy ?? 0)
                          );
                          const {
                            peak: weightedPeakRate,
                            offPeak: weightedOffPeakRate,
                            shoulder: weightedShoulderRate
                          } = calculateWeightedRatesByEnergyFromSegments(
                            segments,
                            dailyPeak,
                            dailyOffPeak,
                            dailyShoulder
                          );
                          const periodForRateDisplay = {
                            ...period,
                            meterPeakRates: undefined,
                            meterOffPeakRates: undefined,
                            meterShoulderRates: undefined
                          };
                          const periodOffer = find(offersWithAllMeters, (offer) => {
                            return !isEmpty(
                              intersection(
                                flatten(map(offer.periods, (period) => map(period.environmentalRatePeriods, 'id'))),
                                map(period.environmentalRatePeriods, 'id')
                              )
                            );
                          });
                          const currentPeriodContestableCosts =
                            calculateDailyPriceByFeePeriod(currentContestableCost) * period.totalNumOfDays;

                          return (
                            <TableRow
                              key={`period-comparison-${periodIndex}`}
                              className={period.isBest === true ? 'highlight' : ''}
                            >
                              <StyledTableCell component="th" scope="row">
                                {periodIndex + 1}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  {periodOffer?.retailer?.name}
                                  {showSegmentHelp &&
                                    (() => {
                                      const segContent = renderSegmentsTooltipContent(
                                        (period?.environmentalRatePeriods ?? []) as SegmentLike[],
                                        `ctc-${periodName}-${periodIndex}`
                                      );
                                      return segContent ? (
                                        <Tooltip title={segContent} arrow placement="right">
                                          <HelpOutlineIcon
                                            sx={{ fontSize: '0.875rem', color: 'text.secondary', cursor: 'help' }}
                                          />
                                        </Tooltip>
                                      ) : null;
                                    })()}
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell>
                                {capitalize(period.offerType === 'FLAT' ? 'SMOOTH' : period.offerType)}
                              </StyledTableCell>
                              <StyledTableCell>
                                {renderRateCell(periodForRateDisplay, 'peak', weightedPeakRate)}
                              </StyledTableCell>
                              <StyledTableCell>
                                {renderRateCell(periodForRateDisplay, 'offPeak', weightedOffPeakRate)}
                              </StyledTableCell>
                              <StyledTableCell>
                                {renderRateCell(periodForRateDisplay, 'shoulder', weightedShoulderRate)}
                              </StyledTableCell>
                              <StyledTableCell>
                                $<NumberText value={period.periodEnergyCosts} precision={0} />
                              </StyledTableCell>
                              <StyledTableCell>
                                $<NumberText value={period.periodRenewalCosts} precision={0} />
                              </StyledTableCell>
                              <StyledTableCell>
                                $
                                <NumberText
                                  value={sum([period.periodRetailerServiceFee, period.periodMeteringCharge])}
                                  precision={0}
                                />
                              </StyledTableCell>
                              <StyledTableCell>
                                $<NumberText value={period.periodContestableCosts} precision={0} />
                              </StyledTableCell>
                              <StyledTableCell>
                                $
                                <NumberText
                                  value={period.periodContestableCosts - sortedPeriods[0].periodContestableCosts}
                                  precision={0}
                                  na="-"
                                />
                              </StyledTableCell>
                              <StyledTableCell>
                                <NumberText value={period.avgContestableCostsPerKwh} precision={2} />
                              </StyledTableCell>
                              {currentContestableCost > 0 && (
                                <>
                                  <StyledTableCell
                                    sx={{
                                      bgcolor:
                                        period.periodContestableCosts > currentPeriodContestableCosts
                                          ? '#ef9a9a'
                                          : '#a5d6a7',
                                      color:
                                        period.periodContestableCosts > currentPeriodContestableCosts
                                          ? '#c62828'
                                          : '#1b5e20'
                                    }}
                                  >
                                    $
                                    <NumberText
                                      value={period.periodContestableCosts - currentPeriodContestableCosts}
                                      precision={0}
                                    />
                                  </StyledTableCell>
                                  <StyledTableCell
                                    sx={{
                                      bgcolor:
                                        period.periodContestableCosts > currentPeriodContestableCosts
                                          ? '#ef9a9a'
                                          : '#a5d6a7',
                                      color:
                                        period.periodContestableCosts > currentPeriodContestableCosts
                                          ? '#c62828'
                                          : '#1b5e20'
                                    }}
                                  >
                                    <NumberText
                                      value={
                                        ((period.periodContestableCosts - currentPeriodContestableCosts) /
                                          currentPeriodContestableCosts) *
                                        100
                                      }
                                      precision={2}
                                    />
                                    %
                                  </StyledTableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </StyledTable>
                  </TableContainer>
                </Box>
              );
            }
          )}
        </SectionContainer>
      </Section>
    </>
  );
};

export default ContractTermComparisonSection;
